import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { MapPin, Loader2, X } from 'lucide-react';

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;

export function AddressAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing address...',
  className,
  disabled = false,
  country = 'ca',
  'data-testid': testId,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions from Geoapify
  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3 || !GEOAPIFY_KEY) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        text,
        apiKey: GEOAPIFY_KEY,
        limit: '5',
        format: 'json',
      });

      if (country) {
        params.append('filter', `countrycode:${country}`);
      }

      params.append('bias', 'proximity:-123.1207,49.2827');

      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?${params}`
      );

      if (!response.ok) throw new Error('Geoapify API error');

      const data = await response.json();
      setSuggestions(data.results || []);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('Address autocomplete error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [country]);

  // Debounced input handler
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange?.(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    const formattedAddress = suggestion.formatted || suggestion.address_line1 || '';
    const result = {
      address: formattedAddress,
      lat: suggestion.lat,
      lng: suggestion.lon,
      components: {
        housenumber: suggestion.housenumber,
        street: suggestion.street,
        city: suggestion.city,
        state: suggestion.state,
        postcode: suggestion.postcode,
        country: suggestion.country,
      },
    };

    if (onSelect) {
      onSelect(result);
    } else {
      onChange?.(formattedAddress);
    }

    setSuggestions([]);
    setShowDropdown(false);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleClear = () => {
    onChange?.('');
    onSelect?.({ address: '', lat: null, lng: null, components: {} });
    setSuggestions([]);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          onBlur={() => {
            setTimeout(() => setShowDropdown(false), 200);
          }}
          placeholder={placeholder}
          className={cn('pr-16', className)}
          disabled={disabled}
          autoComplete="off"
          data-testid={testId}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {value && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-muted rounded"
              tabIndex={-1}
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      {showDropdown && (suggestions.length > 0 || (!loading && value.length >= 3)) && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <button
                key={suggestion.place_id || index}
                type="button"
                className={cn(
                  'w-full px-3 py-2 text-left flex items-start gap-2 hover:bg-accent border-b last:border-0',
                  highlightedIndex === index && 'bg-accent'
                )}
                onClick={() => handleSelect(suggestion)}
              >
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">
                    {suggestion.address_line1 || suggestion.formatted?.split(',')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {suggestion.address_line2 || suggestion.formatted}
                  </p>
                </div>
              </button>
            ))
          ) : (
            <div className="p-3 text-sm text-muted-foreground text-center">
              Адреса не найдены
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
